import React, { useEffect, useState, useMemo } from "react";
import Highcharts from "highcharts";
import rawData from "./data.json";
import { FiFilter, FiRefreshCw } from "react-icons/fi";
import { Container, Card, Form, Button, Badge, InputGroup } from "react-bootstrap";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import "bootstrap/dist/css/bootstrap.min.css";

const MedicareRefinedHistogram = () => {
  const barColor = "#8FCBE6";
  const lineColor = "#8B83A6";

  const colors = {
    primary: "#8B83A6",
    rail: "#E2E8F0",
    textDark: "#1F1F45",
    textMuted: "#6B6B9E",
    border: "#D1D1E9",
    accent: "#E96926" 
  };

  const allFlattenedData = useMemo(() => Object.values(rawData).flat(), []);
  
  const MAX_DATA_VALUE = useMemo(() => {
    const vals = allFlattenedData.map(d => d.medicare_percentage);
    return vals.length > 0 ? Math.ceil(Math.max(...vals)) : 100;
  }, [allFlattenedData]);

  const [range, setRange] = useState([0, MAX_DATA_VALUE]);
  const [tempInputRange, setTempInputRange] = useState([0, MAX_DATA_VALUE]);

  useEffect(() => {
    const filteredValues = allFlattenedData
      .map((item) => item.medicare_percentage)
      .filter((val) => val >= range[0] && val <= range[1])
      .sort((a, b) => a - b);

    const binCount = 35; 
    const binWidth = (range[1] - range[0]) / binCount;
    const histogramData = [];
    const trendLineData = [];

    for (let i = 0; i < binCount; i++) {
      const binStart = range[0] + (i * binWidth);
      const binEnd = binStart + binWidth;
      const count = filteredValues.filter(v => v >= binStart && v < binEnd).length;
      histogramData.push({ x: binStart, x2: binEnd, y: count });
      trendLineData.push([binStart + (binWidth / 2), count]);
    }

    Highcharts.chart("histogram-container", {
      chart: { height: 600, borderRadius: 12, backgroundColor: "#FFFFFF" },
      title: { text: "Medicare Percentage Distribution", align: "left", style: { fontWeight: "700", color: colors.textDark } },
      xAxis: { title: { text: "Medicare Percentage (%)" }, lineColor: colors.border },
      yAxis: { title: { text: "Number of Providers" }, gridLineColor: "#F1F1F9" },
      tooltip: {
        useHTML: true,
        backgroundColor: "white",
        borderColor: "#A9A9A9",
        borderRadius: 7,
        shared: true,
        headerFormat: `<span style="font-size: 14px; font-weight: 600; color: #1E293B;">Medicare Distribution</span><br/><span style="color: ${colors.accent};">─────</span><br/>`,
        pointFormatter: function () {
          const binRange = `${Highcharts.numberFormat(this.x, 1)}% - ${Highcharts.numberFormat(this.x2 || this.x, 1)}%`;
          const totalCount = filteredValues.length;
          const percentageOfTotal = totalCount > 0 ? Highcharts.numberFormat((this.y / totalCount) * 100, 1) : 0;
          if (this.series.type === 'spline') return null;
          return `<div>
                    <div style="color: #64748B; font-size: 11px;">MEDICARE % RANGE</div>
                    <div style="font-size: 16px; font-weight: 700; color: #1E293B;">${binRange}</div>
                  </div>
                  <div style="margin-top: 10px; color: #64748B; font-size: 11px;">PROVIDER COUNT</div>
                  <div style="font-size: 15px; font-weight: 600; color: #1E293B;">${Highcharts.numberFormat(this.y,0,".",",")} Providers</div>
                  <div style="margin-top: 10px; color: #64748B; font-size: 11px;">DISTRIBUTION</div>
                  <div style="font-size: 14px; font-weight: 600; color: #334155;">${percentageOfTotal}% of total selection</div>`;
        },
      },
      plotOptions: {
        column: { color: barColor, pointPadding: 0.05, groupPadding: 0, borderWidth: 0, borderRadius: 4, states: { hover: { brightness: -0.1 } } },
        spline: { color: lineColor, lineWidth: 3, marker: { enabled: false }, states: { hover: { lineWidth: 5, brightness: -0.2 } } }
      },
      series: [
        { name: "Providers", type: "column", data: histogramData, zIndex: 1 },
        { name: "Trend", type: "spline", data: trendLineData, zIndex: 2 }
      ],
      credits: { enabled: false }
    });
  }, [range, allFlattenedData, barColor, lineColor]);

  const handleSliderChange = (val) => {
    setRange(val);
    setTempInputRange(val);
  };

  const handleInputChange = (index, value) => {
    const newTemp = [...tempInputRange];
    newTemp[index] = value === "" ? "" : Number(value);
    setTempInputRange(newTemp);
  };

  const applyFilter = () => setRange([Number(tempInputRange[0]), Number(tempInputRange[1])]);
  const resetFilters = () => {
    const defaultRange = [0, MAX_DATA_VALUE];
    setRange(defaultRange);
    setTempInputRange(defaultRange);
  };

  return (
    <Container fluid className="px-4 py-3" style={{ backgroundColor: "#F9F9FC", minHeight: "100vh" }}>
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
            <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
              <FiFilter size={18} style={{ color: colors.primary }} />
              <Card.Title className="mb-0 fs-6 text-dark" style={{ fontWeight: "600" }}>
                Filter Medicare %
              </Card.Title>
              <Badge bg="light" text="dark" className="ms-2 border">
                {range[0].toLocaleString()}% - {range[1].toLocaleString()}%
              </Badge>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={resetFilters}
              className="d-flex align-items-center gap-1"
              style={{ fontSize: "0.75rem" }}
            >
              <FiRefreshCw size={12} /> Reset
            </Button>
          </div>

          {/* Slider Section */}
          <div className="mt-3 mb-4 px-2">
            <Slider
              range
              min={0}
              max={MAX_DATA_VALUE}
              value={range}
              onChange={handleSliderChange}
              railStyle={{ backgroundColor: colors.rail, height: 4, borderRadius: 2 }}
              trackStyle={[{ backgroundColor: colors.primary, height: 4, borderRadius: 2 }]}
              handleStyle={[
                { borderColor: colors.primary, backgroundColor: "white", height: 16, width: 16, boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)", marginTop: -6, opacity: 1 },
                { borderColor: colors.primary, backgroundColor: "white", height: 16, width: 16, boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)", marginTop: -6, opacity: 1 },
              ]}
            />
          </div>

          {/* Input Controls */}
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column flex-sm-row gap-3">
              <div className="flex-grow-1">
                <Form.Label className="small text-muted mb-1">Min Value</Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-white border-end-0 text-muted">%</InputGroup.Text>
                  <Form.Control
                    type="number"
                    value={tempInputRange[0]}
                    onChange={(e) => handleInputChange(0, e.target.value)}
                    onBlur={applyFilter}
                    onKeyPress={(e) => e.key === "Enter" && applyFilter()}
                    className="border-start-0"
                  />
                </InputGroup>
              </div>

              <div className="text-center d-none d-sm-block align-self-end mb-1">
                <div className="text-muted fs-5">–</div>
              </div>

              <div className="flex-grow-1">
                <Form.Label className="small text-muted mb-1">Max Value</Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-white border-end-0 text-muted">%</InputGroup.Text>
                  <Form.Control
                    type="number"
                    value={tempInputRange[1]}
                    onChange={(e) => handleInputChange(1, e.target.value)}
                    onBlur={applyFilter}
                    onKeyPress={(e) => e.key === "Enter" && applyFilter()}
                    className="border-start-0"
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div id="histogram-container" style={{ width: "100%" }} />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MedicareRefinedHistogram;